import * as yup from 'yup';
import { CronExpressionParser } from 'cron-parser';

// ============================
// YARDIMCI FONKSİYONLAR
// ============================

/**
 * JSON validasyonu için yardımcı fonksiyon
 */
const validateJson = (value) => {
  if (!value || value.trim().length === 0) return true;
  
  try {
    // JavaScript objesi için düzenleme
    const corrected = value.replace(/([{,]\s*)(\w+)\s*:/g, '$1"$2":');
    JSON.parse(corrected);
    return true;
  } catch {
    return false;
  }
};

/**
 * Şifre validasyonu için yardımcı fonksiyon
 */
const validatePassword = (value) => {
  if (value.length < 8) return false;
  if (!/\d/.test(value) || !/[a-zA-Z]/.test(value)) return false;
  return true;
};

/**
 * Cron validasyonu için yardımcı fonksiyon
 * cron-parser kullanarak doğrulama yapar
 */
const validateCron = (value) => {
  if (!value) return false;
  
  try {
    CronExpressionParser.parse(value, {
      tz: 'Europe/Istanbul'
    });
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Durum kodu validasyonu için yardımcı fonksiyon
 */
const validateStatusCodes = (value) => {
  if (!value || value.trim().length === 0) return true;
  
  try {
    const codes = value.split(',').map(Number);
    return codes.every(code => code >= 100 && code < 600);
  } catch {
    return false;
  }
};

/**
 * Host validasyonu için yardımcı fonksiyon
 */
const validateHost = (value) => {
  // URL kontrolü
  try {
    new URL(value);
    return true;
  } catch {
    // IP veya hostname kontrolü
    const hostnameRegex = /^(?=.{1,253}$)(?!\-)([a-zA-Z0-9\-]{1,63}\.)+[a-zA-Z]{2,}$/;
    const ipRegex = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;
    return ipRegex.test(value) || hostnameRegex.test(value);
  }
};

/**
 * HTTP metodları
 */
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'HEAD', 'PATCH', 'OPTIONS'];

/**
 * Anahtar kelime tipleri
 */
const KEYWORD_TYPES = ['txt', 'html', 'json'];

// ============================
// TEMEL ŞEMALAR
// ============================

export const registerFormSchema = yup.object().shape({
  username: yup.string().required('Kullanıcı adınız boş olamaz'),
  email: yup
    .string()
    .email('Lütfen email formatınızı kontrol edin!')
    .required('Email alanı boş olamaz'),
  password: yup
    .string()
    .required('Lütfen şifrenizi giriniz')
    .test('passwordFormat', 'Şifre en az 8 karakterden oluşmalı ve en az 1 harf ve 1 rakam içermelidir', validatePassword),
  agree: yup
    .boolean()
    .isTrue('Gizlilik politikası ve şartlarını onaylanması zorunludur!')
    .required('Gizlilik politikası ve şartlarını onaylanması zorunludur!'),
});

export const loginFormSchema = yup.object().shape({
  email: yup
    .string() 
    .email('Lütfen email formatınızı kontrol edin!')
    .required('Email alanı boş olamaz'),
  password: yup
    .string()
    .required('Lütfen şifrenizi giriniz')
    .test('passwordFormat', 'Şifre en az 8 karakterden oluşmalı ve en az 1 harf ve 1 rakam içermelidir', validatePassword),
});

export const landingFormSchema = yup.object().shape({
  name: yup.string().required('Adınız belirtilmesi zorunludur'),
  email: yup
    .string()
    .email('Lütfen email formatınızı kontrol edin!')
    .required('Email alanı boş olamaz'),
  message: yup.string().required('Mesaj alanı boş olamaz'),
});

export const forgotPasswordFormSchema = yup.object().shape({
  email: yup
    .string()
    .email('Lütfen email formatınızı kontrol edin!')
    .required('Email alanı boş olamaz'),
});

export const resetPasswordFormSchema = yup.object().shape({
  password: yup
    .string()
    .required('Lütfen şifrenizi giriniz')
    .min(8, 'Şifre en az 8 karakterden oluşmalıdır')
    .test('passwordFormat', 'Şifre en az 1 harf ve 1 rakam içermelidir', validatePassword),
  verifyPassword: yup
    .string()
    .required('Lütfen şifrenizi doğrulayın')
    .oneOf([yup.ref('password')], 'Şifreler eşleşmiyor'),
});

// ============================
// ORTAK MONİTÖR ALANLARI
// ============================

/**
 * Tüm monitörler için ortak alanlar
 */
const commonMonitorFields = {
  name: yup.string().required('Ad alanı zorunludur!'),
  cronSchedule: yup
    .string()
    .required('Cron ifadesi zorunludur')
    .test('is-valid-cron', 'Geçersiz cron ifadesi', validateCron),
  failCountRef: yup
    .number()
    .min(1, 'Başarısızlık sayısı en az 1 olmalıdır!')
    .required('Başarısızlık sayısı zorunludur!'),
};

/**
 * HTTP monitörleri için ortak alanlar
 */
const httpMonitorFields = {
  ...commonMonitorFields,
  method: yup
    .string()
    .oneOf([...HTTP_METHODS], 'Geçersiz HTTP metodu!')
    .required('HTTP metodu zorunludur!'),
  host: yup
    .string()
    .url('Geçersiz URL adresi!')
    .required('URL alanı zorunludur!'),
  body: yup
    .string()
    .test('is-json', 'Body JSON formatında olmalıdır!', validateJson),
  headers: yup
    .string()
    .test('is-json', 'Headers JSON formatında olmalıdır!', validateJson),
  timeOut: yup
    .number()
    .typeError('Timeout sayısal bir değer olmalıdır!')
    .positive('Timeout pozitif bir değer olmalıdır!'),
  allowedStatusCodes: yup
    .string()
    .test('is-valid-status-list', 'Geçersiz durum kodları!', validateStatusCodes),
  sslCertControl: yup.boolean().required('SSL sertifika kontrolü zorunludur!'),
  sslCertExpire: yup.boolean().required('SSL sertifika süresi kontrolü zorunludur!'),
  domainExpire: yup.boolean().required('Domain süresi kontrolü zorunludur!'),
};

// ============================
// MONİTÖR ŞEMALARI
// ============================

export const newHttpMonitorFormSchema = yup.object().shape({
  ...httpMonitorFields,
  slowResponseAlertStatus: yup.boolean().required('Yavaş yanıt uyarısı durumu zorunludur!'),
  slowResponseAlertValue: yup
    .string()
    .required('Yavaş yanıt değeri zorunludur!')
    .test('is-valid-number', 'Geçerli bir sayı girin!', (value) => {
      const num = Number(value);
      return !isNaN(num) && Number.isInteger(num);
    }),
});

export const newKeywordMonitorFormSchema = yup.object().shape({
  ...httpMonitorFields,
  keyWordType: yup
    .string()
    .oneOf([...KEYWORD_TYPES], 'Geçersiz anahtar kelime tipi!')
    .required('Anahtar kelime tipi zorunludur!'),
  keyWord: yup
    .string()
    .required('Anahtar kelime alanı boş olamaz!')
    .when('keyWordType', {
      is: 'html',
      then: (schema) => schema.test(
        'is-html',
        'HTML tipi için geçerli bir HTML etiketi girin',
        (value) => {
          if (!value) return false;
          const regex = /^<([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>[\s\S]*<\/\1>$/;
          return regex.test(value.trim());
        }
      ),
    })
    .when('keyWordType', {
      is: 'json',
      then: (schema) => schema.test(
        'is-json',
        'Anahtar kelime JSON formatında olmalıdır!',
        validateJson
      ),
    }),
});

export const newPingMonitorFormSchema = yup.object().shape({
  ...commonMonitorFields,
  host: yup
    .string()
    .test('is-valid-host', 'Geçerli bir IP veya hostname girin!', validateHost)
    .required('Host alanı zorunludur!'),
});

export const newPortMonitorFormSchema = yup.object().shape({
  ...commonMonitorFields,
  host: yup
    .string()
    .test('is-valid-host', 'Geçerli bir IP veya hostname girin!', validateHost)
    .required('Host alanı zorunludur!'),
  port: yup
    .number()
    .typeError('Port sayısal bir değer olmalıdır!')
    .integer('Port tam sayı olmalıdır!')
    .min(0, 'Port 0-65535 arasında olmalıdır!')
    .max(65535, 'Port 0-65535 arasında olmalıdır!')
    .required('Port zorunludur!'),
});

export const newCronJobMonitorFormSchema = yup.object().shape({
  ...commonMonitorFields,
  deviationTime: yup
    .number()
    .typeError('Sapma zamanı sayısal bir değer olmalıdır!')
    .integer('Sapma zamanı tam sayı olmalıdır!')
    .min(0, 'Sapma zamanı 0-59 arasında olmalıdır!')
    .max(59, 'Sapma zamanı 0-59 arasında olmalıdır!')
    .required('Sapma zamanı zorunludur!'),
});